import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import { LocalStorage } from 'quasar';
import { Acl } from 'src/core/acls/index';
import type { dynamicObj } from 'src/pages/assets';
import { allSubOrgsMenu } from 'src/constants';
import {
  isRegionReady,
  setApiRegion,
} from 'src/pages/api-base-url';
import { regionFromOrgs } from 'src/utils/region-sync';
import { getUserDetailFromkey } from 'src/utils/UserDetails';
import { useSettingStore, useDocumentStore } from 'src/store/main';
import { UserOrg, UserSubOrg } from 'src/utils/organization/index';
import { isObjectEmtpy, isUndefinedOrNull, objLen } from 'src/utils/general';
import { UserDetail } from 'src/types';
import { logger } from 'src/utils/logger';

export const useUserStore = defineStore('user', {
  state: () => ({
    userFullName: '' as string,
    organizations: [] as dynamicObj[],
    sub_org_id: '' as string,
    sub_org_name: '',
    main_orgainzation: '',
    medi_orgainzation: 'cb3f58b6-94bb-4d7e-b6c7-4ab5c6fc6645',
    medi_sub_orgainzation: '34115fd9-d157-4ffb-a95b-c6187a03aafc',
    subOrgIdReqPromiseResolve: null as any,
    subOrgIdReqPromise: null as Promise<string> | null,

    sub_OrgReqPromiseResolve: null as any,
    sub_OrgIdReqPromise: null as Promise<boolean> | null,
    allSubOrgsMenu: allSubOrgsMenu,
    allUsers: [] as any,
    allUsersReqPromiseResolve: null as any,
    allUsersReqPromise: null as Promise<any> | null,
    profileReqPromise: null as Promise<void> | null,
    userDetail: {} as UserDetail,
  }),
  getters: {
    getUserName(state) {
      return state.userFullName;
    },
  },
  actions: {
    getUserDetailByKey(key: string) {
      return getUserDetailFromkey(key);
    },

    getOrgId() {
      const user_org_id = this.getUserDetailByKey('org_id');
      return user_org_id;
    },

    getUserId() {
      const user_id = this.getUserDetailByKey('id');
      return user_id;
    },

    isMediUser() {
      const user_org_id = this.getUserDetailByKey('org_id');
      if (
        user_org_id === this.medi_orgainzation &&
        this.sub_org_id == this.medi_sub_orgainzation
      ) {
        return true;
      }
      return false;
    },

    updateUserName() {
      const first_name = getUserDetailFromkey('first_name');
      const last_name = getUserDetailFromkey('last_name');
      this.userFullName = first_name.concat(' ', last_name);
    },

    async getProfile() {
      if (this.profileReqPromise) {
        return this.profileReqPromise;
      }

      this.profileReqPromise = this._executeGetProfile();
      try {
        return await this.profileReqPromise;
      } finally {
        this.profileReqPromise = null;
      }
    },

    async _executeGetProfile() {
      try {
        const { userApiService } = await import('src/services');
        const userDetail = await userApiService.getProfile();

        // The OAuth2 profile endpoint returns { user: {...}, organisations: [...] }.
        // Flatten the nested user fields so getUserDetailByKey('id') etc. continue to work.
        if (userDetail?.user) {
          userDetail.id ??= userDetail.user.id;
          userDetail.is_organisation_admin ??= userDetail.user.is_organisation_admin;
          userDetail.first_name = userDetail.user.first_name;
          userDetail.last_name = userDetail.user.last_name;
        }
        if (userDetail && !userDetail.id && userDetail.user_id) {
          userDetail.id = userDetail.user_id;
        }

        // Merge over existing user_details to preserve id/org_id written by authSdkService.setUserSession().
        const existingDetails = (LocalStorage.getItem('user_details') as Record<string, any>) || {};
        const mergedDetails: Record<string, any> = { ...existingDetails };
        if (userDetail) {
          for (const [key, value] of Object.entries(userDetail)) {
            if (value !== undefined && value !== null) {
              mergedDetails[key] = value;
            }
          }
        }
        LocalStorage.set('user_details', mergedDetails);

        if (!mergedDetails.id) {
          logger.warn('[STORE] getProfile: user_id missing after merge — subsequent API calls may 422');
        }

        // Persist whatever region the profile reports so URL constants pick the
        // right *.docbits.com prefix on the next module load. Cross-region
        // detection (login region vs profile region) lives in prepare-dashboard
        // now. Only persist a region the profile ACTUALLY reports — a profile
        // with no explicit region must not silently flip the user to eu
        // (DOCB-12864). null ⇒ leave the current region untouched.
        const orgs = Array.isArray(userDetail?.organisations)
          ? (userDetail.organisations as Array<{ is_default?: boolean; region?: string }>)
          : [];
        const profileRegion = regionFromOrgs(orgs);
        if (profileRegion) {
          setApiRegion(profileRegion);
        }
      } catch (err) {
        if (!isRegionReady()) {
          setApiRegion('eu');
        }
        logError(err);
        throw { message: 'Error in fetching user details' };
      }
    },

    getParentOrgId() {
      try {
        const userInfo: any = LocalStorage.getItem('user_details');
        if (objLen(userInfo) > 0 && userInfo.hasOwnProperty('organisations')) {
          const organisations = userInfo.organisations;
          if (
            objLen(organisations) > 0 &&
            objLen(organisations[0]) > 0 &&
            organisations[0].hasOwnProperty('parent_org_id')
          ) {
            return organisations[0].parent_org_id
              ? String(organisations[0].parent_org_id)
              : null;
          }
        }
        return '';
      } catch (err) {
        logError(err);
        return '';
      }
    },

    async fetchUserSettings() {
      await this.getProfile();
      await Acl.fetch();
      const settings_store = useSettingStore();
      await settings_store.startSettingsFetch();
      await this.getUserOrganizations();
    },

    async getUserOrganizations() {
      if (!this.sub_OrgIdReqPromise) {
        this.sub_OrgIdReqPromise = new Promise((resolve) => {
          this.sub_OrgReqPromiseResolve = resolve;
          this.fetchSubOrgFromDatabase();
        });
      }
      return this.sub_OrgIdReqPromise;
    },

    async fetchSubOrgFromDatabase() {
      try {
        const organizations = await UserOrg.fetchFromServer();
        logger.debug(
          '[Store] fetchSubOrgFromDatabase - Received organizations:',
          organizations,
        );
        logger.debug('[Store] Is array?', Array.isArray(organizations));

        if (
          organizations &&
          Array.isArray(organizations) &&
          organizations.length > 0
        ) {
          logger.debug('[Store] First organization:', organizations[0]);
          logger.debug(
            '[Store] sub_organisations:',
            organizations[0].sub_organisations,
          );
          logger.debug(
            '[Store] sub_organisations length:',
            organizations[0].sub_organisations?.length,
          );
          this.organizations = organizations as dynamicObj[];
        } else {
          // FIX: Treat empty organizations as non-critical
          // Some users may not have sub-organizations configured
          // Changed from warn to debug - this is expected during initial load before API fetch completes
          logger.debug(
            '[Store] Organizations is empty or not an array - continuing with empty organizations',
          );
          logger.debug(
            '[Store] This is a non-critical condition. User may not have sub-organizations configured yet, or data is still loading.',
          );
          this.organizations = [] as dynamicObj[];
        }

        logger.debug(
          '[Store] ✓ Organizations assigned to store. Store value:',
          this.organizations,
        );

        if (this.organizations && this.organizations.length > 0) {
          logger.debug(
            '[Store] Store organizations[0].sub_organisations:',
            this.organizations[0].sub_organisations,
          );
        }
      } catch (err) {
        logger.error('[Store] ❌ Error fetching organizations:', err);
        logError(err);
        // FIX: Treat fetch error as non-critical
        logger.warn(
          '[Store] Continuing with empty organizations despite fetch error',
        );
        this.organizations = [] as dynamicObj[];
        // Don't re-throw error - allow preparation to continue
      } finally {
        if (this.sub_OrgReqPromiseResolve) {
          this.sub_OrgReqPromiseResolve();
        }
      }
    },

    getMainOrganization() {
      this.main_orgainzation = i18n.t('account_settings');

      if (!isUndefinedOrNull(this.sub_org_name) && this.sub_org_name !== '') {
        this.main_orgainzation = this.sub_org_name;
        return;
      }

      if (!this.organizations || !Array.isArray(this.organizations)) {
        return;
      }

      const first_record = this.organizations[0] as any;
      if (!first_record || Object.keys(first_record).length === 0) {
        return;
      }
      this.main_orgainzation = first_record.name;
    },

    async refreshUserOrganizations() {
      try {
        this.sub_OrgIdReqPromise = null;
        await this.getUserOrganizations();
        await this.refreshAndGetSubOrgId();
      } catch (err) {}
    },

    async performGetSubOrgId() {
      try {
        //Making sure settings are loaded before performing the logic
        const settings_store = useSettingStore();
        await settings_store.startSettingsFetch();

        await this.getUserOrganizations();
        // Use store state directly instead of cache to avoid race condition
        // getSubOrgs() reads from localStorage cache, but store was just populated
        const sub_orgs = this.organizations?.[0]?.sub_organisations || [];
        // if no sub orgs then set main org as default
        if (sub_orgs.length === 0) {
          this.getMainOrganization();
          this.sub_org_id = '';
          return;
        }
        this.sub_org_id = String(await UserSubOrg.get());
        if (isUndefinedOrNull(this.sub_org_id) || !this.sub_org_id) {
          // assigned first organization as default
          this.sub_org_id = UserSubOrg.geSubOrgIdFromOrganizations();
          await UserSubOrg.setSubOrgId(this.sub_org_id);
        }

        let subOrganizations = await UserSubOrg.getSubOrgWithTryOnce(
          this.sub_org_id,
        );
        if (!isObjectEmtpy(subOrganizations)) {
          this.sub_org_name = subOrganizations.name;
        } else {
          // set first sub org as default
          subOrganizations = sub_orgs[0];
          await UserSubOrg.setSubOrgId(subOrganizations.id);
          this.sub_org_name = subOrganizations.name;
        }

        this.getMainOrganization();

        if (!this.sub_org_id || this.sub_org_id === 'null') {
          this.sub_org_id = '';
        }
      } catch (err) {
        logError(err);
      } finally {
        if (this.subOrgIdReqPromiseResolve) {
          this.subOrgIdReqPromiseResolve(this.sub_org_id);
        }
      }
    },

    clearSubOrgIdPromise() {
      this.subOrgIdReqPromiseResolve = null;
      this.subOrgIdReqPromise = null;
    },

    async refreshAndGetSubOrgId() {
      this.clearSubOrgIdPromise();
      await this.getSubOrgId();
    },

    async getSubOrgId() {
      if (this.subOrgIdReqPromise) {
        const document_store = useDocumentStore();
        if (document_store.record.sub_org_id) {
          const document_sub_org_id: string = document_store.record.sub_org_id;
          return document_sub_org_id;
        }
         
        return this.subOrgIdReqPromise;
      }

      this.subOrgIdReqPromise = new Promise((resolve) => {
        this.subOrgIdReqPromiseResolve = resolve;
        this.performGetSubOrgId();
      });

      return this.subOrgIdReqPromise;
    },

    restUserPromise() {
      this.allUsers = [];
      this.allUsersReqPromise = null;
    },

    async getAllUsers(queryParam = '') {
      if (this.allUsersReqPromise) {
         
        return this.allUsersReqPromise;
      }

      this.allUsersReqPromise = new Promise((resolve) => {
        this.allUsersReqPromiseResolve = resolve;
        this.fetchAllUsers(queryParam);
      });

      return this.allUsersReqPromise;
    },

    // DO not use this function directly, use getAllUsers
    async fetchAllUsers(queryParam = '') {
      try {
        // if list is already filled do not call api again and again
        if (this.allUsers.length > 1) {
          return;
        }
        const { userApiService } = await import('src/services');
        this.allUsers = [];
        this.allUsers = await userApiService.getUsers(queryParam);
      } catch (err) {
        logError(err);
        this.allUsers = [];
      } finally {
        if (this.allUsersReqPromiseResolve) {
          this.allUsersReqPromiseResolve(this.allUsers);
        }
      }
    },

    resetUserDetailStore() {
      this.userDetail = {
        first_name: '',
        last_name: '',
        is_organisation_admin: false,
        last_logged_in: null,
        created_on: '',
        email: '',
        id: 0,
        is_system_user: false,
        is_admin: false,
        last_modified_on: '',
        sub_organisations: [],
        groups: [],
      };
    },

    async getUserDetailWithGroupAndSubOrgByUserId(id: string) {
      try {
        // Validate user_id is not empty before making API call
        if (!id || id === '') {
          console.warn(
            '[user_details.store] getUserDetailWithGroupAndSubOrgByUserId called with empty user_id, fetching profile first...',
          );
          await this.getProfile();
          id = this.getUserDetailByKey('id');

          if (!id || id === '') {
            console.warn(
              '[user_details.store] user_id still not available after profile fetch, allowing dashboard to continue with limited features',
            );
            // ✅ FIX: Return early instead of throwing - user_id is not critical for dashboard functionality
            return;
          }
          console.log(
            '[user_details.store] Profile loaded, proceeding with user_id:',
            id,
          );
        }

        console.log(
          '[user_details.store] Fetching user details for user_id:',
          id,
        );
        const { userApiService } = await import('src/services');
        const response =
          await userApiService.getUserDetailsWithGroupAndSubOrgById(id);
        // ✅ FIX: Add null check to prevent crash when response is null (corrupted localStorage)
        if (!response) {
          console.warn('[user_details.store] getUserDetailsWithGroupAndSubOrgById returned null - API call may have failed silently');
          return;
        }
        if (response.success === false) {
          throw response;
        }
        if (response.user_detail) {
          this.userDetail = response.user_detail;
        }
      } catch (error) {
        throw error;
      }
    },
  },
});
